/**
 * React Query hooks for public table view API calls.
 *
 * Keys are loaded from the bootstrap endpoint on first use and stored in
 * module-level memory — no re-fetch per component, no env var per table.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { isUserLoggedIn } from '../utils/utils';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

// In-memory key store: { [tableSlug]: "uuid-string" }
let _keyStore = null;
let _keyFetchPromise = null;

async function fetchKeys() {
  const resp = await apiHelper.publicApiCall(
    `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}/keys/`
  );
  if (!resp.ok) return {};
  const data = await resp.json();
  return data.tables || {};
}

async function getKeyStore() {
  if (_keyStore) return _keyStore;
  if (_keyFetchPromise) return _keyFetchPromise;
  _keyFetchPromise = fetchKeys().then((keys) => {
    _keyStore = keys;
    _keyFetchPromise = null;
    return keys;
  });
  return _keyFetchPromise;
}

/**
 * The public access key for one table.
 *
 * Every public endpoint validates this header, including the action-submit one
 * — a request without it is refused as 404, which is indistinguishable from a
 * missing record and gives no clue what is wrong. Exposed so writes can send
 * the same key the reads already do, rather than each caller reimplementing
 * the store.
 */
export async function getPublicAccessKey(tableSlug) {
  const keys = await getKeyStore();
  return keys[tableSlug] || '';
}

/** Clears the in-memory key store (e.g. after a key rotation event). */
export function invalidatePublicKeyStore() {
  _keyStore = null;
}

/* The error classes a binding fetch can end in. Mirrors the taxonomy
 * usePublicAction.runAction already uses for the register button, because a
 * read that fails and a write that fails owe the reader the same distinctions:
 * "sign in" is not "we broke", and neither is "you have nothing".
 *
 * Every thrown error carries `.kind`; `.status` is present whenever there was
 * a response to take one from.
 */
export const BINDING_ERROR = {
  AUTH:     'auth',      // 401 — a person must sign in. Not a failure.
  NOT_FOUND: 'not_found', // 404 — table gone, or the access key was refused.
  THROTTLED: 'throttled', // 429 — expected under burst; waited out, then retried.
  SERVER:    'server',    // any other non-2xx, including a non-JSON body.
  NETWORK:   'network',   // never reached the server at all.
};

function bindingError(kind, message, extra = {}) {
  const err = new Error(message);
  err.kind = kind;
  Object.assign(err, extra);
  return err;
}

async function publicGet(path, tableSlug) {
  const keys = await getKeyStore();
  const key = keys[tableSlug] || '';

  let resp;
  try {
    resp = await apiHelper.publicApiCall(path, {
      method: 'GET',
      cache: 'no-cache',   // bypass browser HTTP cache so normal refresh gets fresh data
      // An owner-scoped view is answered from the visitor's SESSION, not from
      // the access key — the key says which table may be read, the cookie says
      // whose rows. Without this the server sees an anonymous request and
      // refuses. Harmless on every other public view: the server ignores the
      // cookie unless the view opted in.
      credentials: 'include',
      headers: { 'X-Public-Access-Key': key },
    });
  } catch (e) {
    // Never reached the server: offline, DNS, CORS, connection reset. Said as
    // such rather than as a generic failure that sends someone hunting a
    // server that was never asked anything.
    if (e && e.name === 'AbortError') throw e;
    throw bindingError(BINDING_ERROR.NETWORK,
      'Could not reach the server. Check your connection and try again.');
  }

  if (resp.status === 401) {
    // NOT a failure. The rows exist and belong to someone — the caller has to
    // offer a sign-in, not an error page. Flagged rather than thrown as a
    // generic error so a section can tell the two apart.
    //
    // A 401 here when the app believes this visitor IS signed in means the
    // session died since the page loaded, so the app's own session dialog is
    // raised — the same one apiCall raises. publicApiCall is a bare fetch and
    // dispatches nothing, which is how an expiry on this path used to pass in
    // silence.
    //
    // ONLY WHEN THE SERVER SAYS SO. A 401 is not one fact, it is two, and they
    // want opposite things from the visitor:
    //
    //   {"detail":"session_expired"}  — you had a session and it went. Sign in
    //                                   again and you are back where you were.
    //   {"requires_auth":true}        — you may not read these rows. Signing in
    //                                   again changes nothing.
    //
    // Treating the second as the first is a circle with no way out: the dialog
    // sends them to /sign-in?next=…, the sign-in page finds a signed-in user
    // and honours ?next=, the page asks again, and the same 401 comes back.
    // That is the my-registrations loop. So the dialog is raised only for the
    // body that actually claims an expiry — and a body that will not parse is
    // an unknown, which stays quiet rather than guessing the worse of the two.
    const refusal = await resp.json().catch(() => null);
    const saysExpired = Boolean(refusal && (
      refusal.detail === 'session_expired'
      || refusal.reason === 'session_invalid_or_expired'
    ));

    if (saysExpired) {
      // ASKED FRESH, NEVER FROM THE CACHE. isUserLoggedIn holds its answer for
      // a few seconds, and that cache is the other half of the loop: a "yes"
      // taken from it while the server is refusing is precisely the
      // disagreement that keeps the circle turning. The round trip is worth it
      // here — this runs once, on a refusal, not on the happy path.
      //
      // Asked rather than inferred from document.cookie: `sessionid` is
      // HttpOnly and invisible to JS, and `csrftoken` — the only readable one —
      // is also set by a FAILED sign-in, which would raise "session expired" at
      // someone who never had a session. Fire-and-forget: the refusal below is
      // thrown immediately either way, and a visitor who was never signed in
      // simply gets no dialog.
      isUserLoggedIn(undefined, { fresh: true })
        .then((user) => {
          if (!user) return;
          window.dispatchEvent(new CustomEvent('session-expired', {
            detail: { title: 'Session expired', message: 'Login again to continue.' },
          }));
        })
        .catch(() => { /* cannot tell: stay quiet rather than guess */ });
    }

    throw bindingError(BINDING_ERROR.AUTH, 'Sign in required',
      { status: 401, requiresAuth: true });
  }

  if (resp.status === 404) {
    throw bindingError(BINDING_ERROR.NOT_FOUND, 'Not found', { status: 404 });
  }

  if (resp.status === 429) {
    // Throttled, not refused. The public API allows a burst of reads a second
    // and a page with several bindings sends them at once, so this is an
    // ordinary answer to wait out — see `retry` in the query options below.
    const header = Number(resp.headers?.get?.('Retry-After'));
    throw bindingError(BINDING_ERROR.THROTTLED, 'The server is busy. Retrying…',
      { status: 429, retryAfter: header > 0 ? header : 1 });
  }

  if (!resp.ok) {
    throw bindingError(BINDING_ERROR.SERVER,
      `The server could not answer this (error ${resp.status}).`, { status: resp.status });
  }

  // A body that is not JSON is a server error page, a proxy notice or a gateway
  // timeout. Its TEXT must never reach the visitor — that is how a screen ends
  // up showing raw HTML — so it becomes a plain sentence and the status is kept
  // for whoever reads the report.
  const body = await resp.json().catch(() => null);
  if (body === null) {
    throw bindingError(BINDING_ERROR.SERVER,
      `The server could not answer this (error ${resp.status}).`, { status: resp.status });
  }
  return body;
}

/**
 * One page of a public table list, outside React Query — for callers that
 * page or post-process the rows themselves. Same endpoint, access key and
 * error kinds as usePublicTableList.
 *
 * @param {string} tableSlug
 * @param {Object} params - { page, page_size, search, sort, sort_dir, ...filter[x] }
 */
export function fetchPublicTableList(tableSlug, params = {}) {
  const queryString = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''))
  ).toString();

  const path = `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}/${tableSlug}/${queryString ? '?' + queryString : ''}`;
  return publicGet(path, tableSlug);
}

/**
 * Builds react-query options for a public table list fetch, usable directly
 * with useQuery() or as one entry in useQueries({ queries: [...] }) — the
 * latter is required when the number of bindings is dynamic (rules of hooks
 * forbid calling useQuery a variable number of times).
 *
 * @param {string} tableSlug
 * @param {Object} params - { page, search, sort, sort_dir, ...filter[x] }
 */
export function publicTableListQueryOptions(tableSlug, params = {}) {
  return {
    queryKey: ['public-table-list', tableSlug, params],
    queryFn: () => fetchPublicTableList(tableSlug, params),
    staleTime: 0,
    // A throttle is the one failure worth retrying: the server said "later",
    // not "no". Everything else is returned to the caller on the first answer,
    // as before — a 401 retried is still a 401, and retrying a 500 three times
    // only makes the reader wait longer for the same message.
    retry: (failureCount, error) => error?.kind === BINDING_ERROR.THROTTLED && failureCount < 2,
    retryDelay: (failureCount, error) => Math.max(1, Number(error?.retryAfter) || 1) * 1000,
  };
}

/**
 * Fetches paginated records for a public table list page.
 *
 * @param {string} tableSlug
 * @param {Object} params - { page, search, sort, sort_dir, ...filter[x] }
 */
export function usePublicTableList(tableSlug, params = {}) {
  return useQuery({
    ...publicTableListQueryOptions(tableSlug, params),
    placeholderData: keepPreviousData,
  });
}

/**
 * One public record detail, outside React Query — for callers that read a
 * handful of records of their own choosing. Same endpoint, access key and
 * error kinds as usePublicRecordDetail.
 */
export function fetchPublicRecordDetail(tableSlug, recordId) {
  const path = `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}/${tableSlug}/${recordId}/`;
  return publicGet(path, tableSlug);
}

/**
 * Fetches a single public record detail.
 *
 * @param {string} tableSlug
 * @param {string|number} recordId
 * @param {Object} options - { previewKey } optional preview access key
 */
export function usePublicRecordDetail(tableSlug, recordId, { previewKey } = {}) {
  const qs = previewKey ? `?pk=${encodeURIComponent(previewKey)}` : '';
  const path = `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}/${tableSlug}/${recordId}/${qs}`;

  return useQuery({
    queryKey: ['public-record-detail', tableSlug, recordId, previewKey],
    queryFn: () => publicGet(path, tableSlug),
    staleTime: 0,
    retry: false,
    enabled: !!recordId,
  });
}
